/**
 * Confirmation View Logic
 *
 * Task: T-P4-D2-F6-FE-2
 *
 * Pure functions to determine which confirmation UI to show based on
 * booking status and service configuration.
 *
 * View Priority Order:
 * 1. Booking status (if booking exists) - takes precedence
 * 2. Service configuration (requiresApproval, paymentRequirement)
 * 3. Default (REVIEW_DIRECT)
 */

import type { WebAppFlowContextResponse } from '../../api/types';
import type { ConfirmationView, ViewConfig } from './types';
import { isKnownBookingStatus, requiresOnlinePayment } from './types';

// Re-export types for convenience
export type { ConfirmationView, ViewConfig } from './types';

/**
 * Determines which confirmation view to show based on flow context.
 *
 * @param flowContext - The current flow context from useFlowContext
 * @returns The view to render
 *
 * Logic:
 * 1. If booking exists with known status → show status-based view
 * 2. If booking exists with unknown status → CONFIRMED (safe fallback)
 * 3. If requiresApproval → REVIEW_APPROVAL
 * 4. If online payment required (DEPOSIT/FULL_PAYMENT) → REVIEW_PAYMENT
 * 5. Otherwise → REVIEW_DIRECT (PAY_AT_SESSION and NONE treated as direct)
 */
export function getConfirmationView(
  flowContext: WebAppFlowContextResponse | undefined,
): ConfirmationView {
  // Safe fallback when no context available
  if (!flowContext) {
    return 'REVIEW_DIRECT';
  }

  const booking = flowContext.flow?.booking;
  const service = flowContext.context?.service;

  // Priority 1: Check existing booking status
  if (booking?.status) {
    if (isKnownBookingStatus(booking.status)) {
      // Map known statuses to views
      switch (booking.status) {
        case 'PENDING_APPROVAL':
          return 'PENDING';
        case 'CONFIRMED':
          return 'CONFIRMED';
        case 'PENDING_PAYMENT':
          return 'AWAITING_PAYMENT';
        case 'CANCELLED':
          // The exact request-withdrawal copy promises that nothing was charged.
          // Preserve it only for statuses that prove that statement; older confirm
          // links for paid/refunded/uncertain cancellations get neutral copy.
          return booking.paymentStatus === 'UNPAID'
            || booking.paymentStatus === 'WAIVED'
            || booking.paymentStatus === 'FAILED'
            ? 'CANCELLED'
            : 'CANCELLED_BOOKING';
      }
    } else {
      // Unknown status with booking exists - safe fallback
      // Don't ask user to do more steps; treat as confirmed
      return 'CONFIRMED';
    }
  }

  // Priority 2: Check service configuration (REVIEW step)
  if (service?.requiresApproval) {
    return 'REVIEW_APPROVAL';
  }

  // Check if online payment is required (DEPOSIT or FULL_PAYMENT)
  // PAY_AT_SESSION means payment happens in person, not online
  if (requiresOnlinePayment(service?.paymentRequirement)) {
    return 'REVIEW_PAYMENT';
  }

  // Default: Direct confirmation (no approval, no online payment)
  return 'REVIEW_DIRECT';
}

/**
 * View configuration lookup table.
 * Returns UI configuration for each view type.
 */
const VIEW_CONFIGS: Record<ConfirmationView, ViewConfig> = {
  REVIEW_DIRECT: {
    view: 'REVIEW_DIRECT',
    title: 'Review Your Booking',
    subtitle: 'Please confirm your details below',
    buttonText: 'Confirm Booking',
    buttonAction: 'confirm',
  },
  REVIEW_PAYMENT: {
    view: 'REVIEW_PAYMENT',
    title: 'Review Your Booking',
    subtitle: 'Payment required to confirm',
    buttonText: 'Continue to Payment',
    buttonAction: 'payment',
  },
  REVIEW_APPROVAL: {
    view: 'REVIEW_APPROVAL',
    title: 'Review Your Request',
    subtitle: 'This booking requires approval',
    buttonText: 'Submit Request',
    buttonAction: 'submit',
  },
  PENDING: {
    view: 'PENDING',
    // The website's words (web-sign-in-checkout D10); ConfirmPage names the studio in its subtitle.
    title: 'Request sent',
    subtitle: "The studio will confirm shortly. We'll message you the moment it's approved.",
    buttonText: 'Done',
    buttonAction: 'done',
    showPendingAnimation: true,
  },
  CONFIRMED: {
    view: 'CONFIRMED',
    title: 'Booking Confirmed!',
    subtitle: 'A confirmation has been sent to Telegram',
    buttonText: 'Done',
    buttonAction: 'done',
    showSuccessAnimation: true,
  },
  CANCELLED: {
    view: 'CANCELLED',
    title: 'Request cancelled',
    subtitle: 'This request is no longer active. Nothing was charged.',
    buttonText: 'Done',
    buttonAction: 'done',
  },
  CANCELLED_BOOKING: {
    view: 'CANCELLED_BOOKING',
    title: 'Booking cancelled',
    subtitle: 'This booking is no longer active. Check your messages for any payment or refund updates.',
    buttonText: 'Done',
    buttonAction: 'done',
  },
  AWAITING_PAYMENT: {
    view: 'AWAITING_PAYMENT',
    title: 'Awaiting Payment',
    subtitle: 'Please complete payment to confirm your booking',
    buttonText: 'Continue to Payment',
    buttonAction: 'redirect',
  },
};

/**
 * Returns the UI configuration for a given view type.
 *
 * @param view - The confirmation view type
 * @returns Configuration object with title, button text, actions, etc.
 */
export function getViewConfig(view: ConfirmationView): ViewConfig {
  return VIEW_CONFIGS[view];
}
